import React, { useState, useEffect } from 'react';
import { sameOriginWs } from '../utils/wsUrl';

// IMPORTANT: Using the correct configuration from your .env file
const API_BASE = process.env.REACT_APP_API_URL || '/';
const STRATEGIES_PATH = '/strategies';
const API_URL = `${API_BASE}${STRATEGIES_PATH}`;

// WebSocket configuration from your .env
const WS_URL = process.env.REACT_APP_WS_URL || sameOriginWs('/ws/strategy-alerts/');

const StrategiesHistoricalData = () => {
  const [loading, setLoading] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [signalHistory, setSignalHistory] = useState([]);
  const [error, setError] = useState('');
  const [showDebug, setShowDebug] = useState(false);
  const [rawData, setRawData] = useState(null);

  // Load strategies when component mounts
  useEffect(() => {
    console.log('[Historical] Component mounted');
    console.log('[Historical] API URL:', API_URL);
    loadBulkStrategies();
  }, []);

  // Load signal history when strategy changes
  useEffect(() => {
    if (selectedStrategy) {
      loadSignalHistory(selectedStrategy);
    }
  }, [selectedStrategy]);

  const loadBulkStrategies = async () => {
    const url = `${API_URL}/api/bulk-strategy/list/`;
    console.log('[Historical] Loading strategies from:', url);
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(url);
      console.log('[Historical] Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      
      const data = await response.json();
      console.log('[Historical] Strategies loaded:', data);
      
      // Handle the response structure
      const strategiesList = data.bulk_strategies || data.strategies || [];
      setStrategies(strategiesList);
      
      // Auto-select first strategy if available
      if (strategiesList.length > 0) {
        setSelectedStrategy(strategiesList[0].bulk_strategy_id || strategiesList[0].strategy_id);
      } else {
        setError('No strategies found. Please create a strategy first.');
      }
      
    } catch (err) {
      console.error('[Historical] Error loading strategies:', err);
      setError(`Failed to load strategies: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadSignalHistory = async (strategyId) => {
    console.log(`[Historical] Loading signals for strategy: ${strategyId}`);
    
    setLoading(true);
    setError('');
    setRawData(null);
    
    try {
      // Try multiple endpoints to ensure we get all data
      const endpoints = [
        {
          url: `${API_URL}/api/bulk-strategy/signal-timeline/${strategyId}/`,
          name: 'timeline'
        },
        {
          url: `${API_URL}/api/bulk-strategy/signals/history/${strategyId}/?include_sub_strategies=true&limit=500`,
          name: 'history'
        }
      ];
      
      const results = {};
      
      for (const endpoint of endpoints) {
        try {
          console.log(`[Historical] Fetching from ${endpoint.name}:`, endpoint.url);
          const response = await fetch(endpoint.url);
          
          if (response.ok) {
            const data = await response.json();
            results[endpoint.name] = data;
            console.log(`[Historical] ${endpoint.name} data:`, data);
          } else {
            console.log(`[Historical] ${endpoint.name} returned:`, response.status);
          }
        } catch (err) {
          console.log(`[Historical] ${endpoint.name} error:`, err.message);
        }
      }
      
      setRawData(results);
      
      // Process the data
      const processed = processHistoricalData(results);
      setSignalHistory(processed);
      
      if (processed.length === 0) {
        setError('No signals found for this strategy. Send some signals to see them here.');
      }
      
    } catch (err) {
      console.error('[Historical] Error loading signals:', err);
      setError(`Failed to load signals: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const processHistoricalData = (data) => {
    const allTrades = [];
    const signalMap = new Map();
    
    // Process timeline data
    if (data.timeline && data.timeline.trades) {
      data.timeline.trades.forEach((trade, idx) => {
        const processedTrade = {
          id: `trade-${idx}`,
          tradeNumber: trade.trade_number || idx + 1,
          date: trade.date,
          status: trade.exit_time ? 'CLOSED' : 'OPEN',
          direction: trade.direction,
          signals: {},
          entryTime: trade.entry_time,
          exitTime: trade.exit_time,
          entryPrice: trade.entry_price,
          exitPrice: trade.exit_price,
          holdMinutes: trade.hold_minutes,
          pnl: trade.pnl
        };
        
        // Add signals
        if (trade.signals && Array.isArray(trade.signals)) {
          trade.signals.forEach(sig => {
            processedTrade.signals[sig.name] = sig.timestamp;
            signalMap.set(sig.name, true);
          });
        }
        
        allTrades.push(processedTrade);
      });
    }
    
    // Process history data - including partial trades
    if (data.history) {
      // Process main signals
      if (data.history.signals && Array.isArray(data.history.signals)) {
        const ungroupedSignals = [];
        
        data.history.signals.forEach(sig => {
          const signalName = extractSignalName(sig);
          const timestamp = sig.timestamp;
          
          if (signalName && timestamp) {
            ungroupedSignals.push({
              name: signalName,
              timestamp: timestamp,
              data: sig
            });
            signalMap.set(signalName, true);
          }
        });
        
        // Group signals by time windows (30 minutes) for partial trades
        const timeWindows = groupSignalsByTime(ungroupedSignals);
        
        timeWindows.forEach((window, idx) => {
          const partialTrade = {
            id: `partial-${idx}`,
            tradeNumber: `P${idx + 1}`,
            date: new Date(window[0].timestamp).toLocaleDateString('en-US', { 
              day: '2-digit', 
              month: 'short' 
            }),
            status: 'PARTIAL',
            direction: 'PENDING',
            signals: {},
            entryTime: '',
            exitTime: '',
            entryPrice: '',
            exitPrice: '',
            holdMinutes: '',
            pnl: ''
          };
          
          window.forEach(sig => {
            partialTrade.signals[sig.name] = sig.timestamp;
          });
          
          // Only add if this partial trade has signals not in complete trades
          if (Object.keys(partialTrade.signals).length > 0) {
            allTrades.push(partialTrade);
          }
        });
      }
      
      // Process sub-strategy signals
      if (data.history.sub_strategy_signals && Array.isArray(data.history.sub_strategy_signals)) {
        data.history.sub_strategy_signals.forEach(sig => {
          const signalName = extractSignalName(sig);
          if (signalName) {
            signalMap.set(signalName, true);
          }
        });
      }
    }
    
    console.log(`[Historical] Processed ${allTrades.length} trades, ${signalMap.size} unique signals`);
    
    // Store signal types for column generation
    allTrades.signalTypes = Array.from(signalMap.keys()).sort();
    
    return allTrades;
  };

  const extractSignalName = (sig) => {
    if (!sig) return null;
    
    // Try different fields
    return sig.signal ||
           sig.signal_type ||
           (sig.data && sig.data.signal) ||
           (sig.signal_data && sig.signal_data.signal) ||
           (sig.data && sig.data.early_signal) ||
           (sig.data && sig.data.confirmation_signal) ||
           null;
  };

  const groupSignalsByTime = (signals, windowMinutes = 30) => {
    const windows = [];
    const windowMs = windowMinutes * 60 * 1000;
    
    const sorted = signals.sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );
    
    let currentWindow = [];
    let windowStart = null;
    
    sorted.forEach(sig => {
      const sigTime = new Date(sig.timestamp).getTime();
      
      if (!windowStart || sigTime - windowStart > windowMs) {
        if (currentWindow.length > 0) {
          windows.push(currentWindow);
        }
        currentWindow = [sig];
        windowStart = sigTime;
      } else {
        currentWindow.push(sig);
      }
    });
    
    if (currentWindow.length > 0) {
      windows.push(currentWindow);
    }
    
    return windows;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return '';
    }
  };

  const formatPrice = (price) => {
    if (price === '' || price === null || price === undefined) return '';
    return typeof price === 'number' ? price.toFixed(2) : price;
  };

  const handleExport = async () => {
    if (!selectedStrategy) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/bulk-strategy/export-timeline/${selectedStrategy}/`
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedStrategy}_signals_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Export failed');
    }
  };

  // Get all unique signal types for columns
  const getAllSignalTypes = () => {
    const types = new Set();
    signalHistory.forEach(trade => {
      Object.keys(trade.signals || {}).forEach(sig => types.add(sig));
    });
    return Array.from(types).sort();
  };

  const signalColumns = getAllSignalTypes();

  // Styles
  const styles = {
    container: {
      padding: '20px',
      backgroundColor: '#1a1d29',
      minHeight: '100vh',
      color: '#e4e6eb',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '15px',
      borderBottom: '1px solid #2d3142'
    },
    title: {
      fontSize: '24px',
      fontWeight: '500',
      margin: 0
    },
    apiInfo: {
      fontSize: '11px',
      color: '#71767b',
      marginTop: '4px',
      fontFamily: 'monospace'
    },
    controls: {
      display: 'flex',
      gap: '10px',
      alignItems: 'center'
    },
    select: {
      padding: '8px 12px',
      backgroundColor: '#242837',
      color: '#e4e6eb',
      border: '1px solid #3d4152',
      borderRadius: '4px',
      fontSize: '14px',
      minWidth: '200px'
    },
    button: {
      padding: '8px 16px',
      backgroundColor: '#00d4ff',
      color: '#000',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '14px'
    },
    tableWrapper: {
      backgroundColor: '#242837',
      borderRadius: '8px',
      overflow: 'auto',
      border: '1px solid #2d3142'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '1200px'
    },
    th: {
      padding: '10px 8px',
      backgroundColor: '#2d3142',
      color: '#9ca3af',
      textAlign: 'center',
      fontSize: '12px',
      fontWeight: '600',
      borderRight: '1px solid #1a1d29',
      position: 'sticky',
      top: 0,
      zIndex: 1
    },
    td: {
      padding: '8px',
      borderBottom: '1px solid #2d3142',
      borderRight: '1px solid #2d3142',
      fontSize: '12px',
      textAlign: 'center'
    },
    signalTime: {
      color: '#00d4ff',
      fontFamily: 'monospace',
      fontSize: '11px'
    },
    statusBadge: {
      padding: '2px 8px',
      borderRadius: '3px',
      fontSize: '11px',
      fontWeight: '600'
    },
    error: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      padding: '12px',
      borderRadius: '4px',
      color: '#fca5a5',
      marginBottom: '20px'
    },
    debug: {
      backgroundColor: '#0f1419',
      border: '1px solid #2d3142',
      borderRadius: '4px',
      padding: '12px',
      marginBottom: '20px',
      fontSize: '11px',
      fontFamily: 'monospace',
      maxHeight: '200px',
      overflow: 'auto'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Historical Data</h1>
          <div style={styles.apiInfo}>
            API: {API_URL} | WS: {WS_URL}
          </div>
        </div>
        <div style={styles.controls}>
          <select
            style={styles.select}
            value={selectedStrategy}
            onChange={(e) => setSelectedStrategy(e.target.value)}
            disabled={loading}
          >
            <option value="">Select Strategy</option>
            {strategies.map(s => (
              <option key={s.bulk_strategy_id} value={s.bulk_strategy_id}>
                {s.bulk_strategy_id}
              </option>
            ))}
          </select>
          
          <button 
            style={styles.button}
            onClick={() => loadSignalHistory(selectedStrategy)}
            disabled={!selectedStrategy || loading}
          >
            Refresh
          </button>
          
          <button 
            style={styles.button}
            onClick={handleExport}
            disabled={!selectedStrategy || loading}
          >
            Export Excel
          </button>
          
          <label style={{ fontSize: '12px', color: '#71767b' }}>
            <input 
              type="checkbox" 
              checked={showDebug}
              onChange={(e) => setShowDebug(e.target.checked)}
            /> 
            Debug
          </label>
        </div>
      </div>

      {error && (
        <div style={styles.error}>
          ⚠️ {error}
        </div>
      )}

      {showDebug && rawData && (
        <div style={styles.debug}>
          <strong>Debug Information:</strong><br/>
          Strategy: {selectedStrategy}<br/>
          Trades Found: {rawData.timeline?.trades?.length || 0}<br/>
          Signals Found: {rawData.history?.signals?.length || 0}<br/>
          Sub-Strategy Signals: {rawData.history?.sub_strategy_signals?.length || 0}<br/>
          Signal Types: {signalColumns.join(', ')}<br/>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px', color: '#71767b' }}>
          Loading signal history...
        </div>
      ) : signalHistory.length > 0 ? (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Trade</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Direction</th>
                
                {/* Dynamic signal columns */}
                {signalColumns.map(signal => (
                  <th key={signal} style={styles.th}>{signal}</th>
                ))}
                
                <th style={styles.th}>Entry Time</th>
                <th style={styles.th}>Exit Time</th>
                <th style={styles.th}>Duration</th>
                <th style={styles.th}>Entry Price</th>
                <th style={styles.th}>Exit Price</th>
                <th style={styles.th}>P&L</th>
              </tr>
            </thead>
            <tbody>
              {signalHistory.map(trade => (
                <tr key={trade.id}>
                  <td style={styles.td}>{trade.tradeNumber}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: trade.status === 'CLOSED' ? 'rgba(16, 185, 129, 0.1)' :
                                     trade.status === 'OPEN' ? 'rgba(245, 158, 11, 0.1)' :
                                     'rgba(107, 114, 128, 0.1)',
                      color: trade.status === 'CLOSED' ? '#10b981' :
                             trade.status === 'OPEN' ? '#f59e0b' : '#6b7280'
                    }}>
                      {trade.status}
                    </span>
                  </td>
                  <td style={styles.td}>{trade.date}</td>
                  <td style={{
                    ...styles.td,
                    color: trade.direction === 'LONG' ? '#10b981' :
                           trade.direction === 'SHORT' ? '#ef4444' : '#6b7280',
                    fontWeight: '600'
                  }}>
                    {trade.direction}
                  </td>
                  
                  {/* Signal columns */}
                  {signalColumns.map(signal => (
                    <td key={signal} style={styles.td}>
                      {trade.signals[signal] && (
                        <span style={styles.signalTime}>
                          {formatTime(trade.signals[signal])}
                        </span>
                      )}
                    </td>
                  ))}
                  
                  <td style={styles.td}>
                    {trade.entryTime && (
                      <span style={styles.signalTime}>
                        {formatTime(trade.entryTime)}
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {trade.exitTime && (
                      <span style={styles.signalTime}>
                        {formatTime(trade.exitTime)}
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {trade.holdMinutes && `${trade.holdMinutes}m`}
                  </td>
                  <td style={styles.td}>{formatPrice(trade.entryPrice)}</td>
                  <td style={styles.td}>{formatPrice(trade.exitPrice)}</td>
                  <td style={{
                    ...styles.td,
                    color: trade.pnl > 0 ? '#10b981' :
                           trade.pnl < 0 ? '#ef4444' : '#6b7280',
                    fontWeight: trade.pnl ? '600' : '400'
                  }}>
                    {trade.pnl && formatPrice(trade.pnl)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px', color: '#71767b' }}>
          {selectedStrategy ? 
            'No signals found for this strategy. Send signals to see them here.' :
            'Select a strategy to view signal history'
          }
        </div>
      )}
    </div>
  );
};

export default StrategiesHistoricalData;
